import { useContext, useEffect, useMemo, useState } from "react";
import AntdTable from "../../../antd/Table/Table";
import { filtrationcontext } from "../../../../context/Filtercontext";
import trans, { getLateLimitInMinutes, isRTL } from "../../../../utils";
import { exportContext } from "../../../../context/ExportDataContext";
import { Image, Tag, Typography } from "antd";
import axios from "axios";
import { useReportTableColumns } from "../../ReportColumns/reportColumns";

const DailyAttendanceReportTable = ({
  startDate,
  endDate,
  currentPage,
  setCurrentPage,
  data,
  isLoading,
  selectedids,
  filters,
}) => {
  const { setFileName, setExportIds, setStartDateE, setEndDateE, setFilters } =
    useContext(exportContext);
  const {
    setSort: SetSortFilter,
    limit,
    setLimit,
  } = useContext(filtrationcontext);
  const [addresses, setAddresses] = useState({});
  const [loadingAddress, setLoadingAddress] = useState({});

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      localStorage.setItem("page", 1);
    };
  }, [SetSortFilter]);

  const EMPTY_VALUE = "—";

  const formatShiftClockTime = (value) => {
    if (!value || value === "N/A" || value === "0") return EMPTY_VALUE;
    const raw = String(value).trim();
    const timePart = raw.includes(" ") ? raw.split(" ").pop() : raw;
    return timePart.length >= 5 ? timePart.slice(0, 5) : timePart;
  };

  const fetchAddress = async (location, cacheKey) => {
    if (!location?.latitude || !location?.longitude || loadingAddress[cacheKey]) {
      return;
    }
    setLoadingAddress((prev) => ({ ...prev, [cacheKey]: true }));
    try {
      const lang = localStorage.getItem("lang") === "ar" ? "ar" : "en";
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse.php?lat=${location.latitude}&lon=${location.longitude}&zoom=18&format=jsonv2&accept-language=${lang}`
      );
      setAddresses((prev) => ({
        ...prev,
        [cacheKey]: response.data?.display_name || trans("Address not found"),
      }));
    } catch (error) {
      setAddresses((prev) => ({
        ...prev,
        [cacheKey]: trans("Address not found"),
      }));
    } finally {
      setLoadingAddress((prev) => ({ ...prev, [cacheKey]: false }));
    }
  };

  const renderLocation = (location, record, type) => {
    if (!location?.coordinates && !location?.mapUrl) return EMPTY_VALUE;
    const cacheKey = `${record?.id}-${type}`;
    const address = addresses[cacheKey];
    const isLoading = loadingAddress[cacheKey];

    return (
      <div className="d-flex flex-column gap-1">
        <span>{location.coordinates || EMPTY_VALUE}</span>
        {address ? (
          <Typography.Text
            ellipsis={{ tooltip: address }}
            style={{ maxWidth: 220, display: "block" }}
          >
            {address}
          </Typography.Text>
        ) : (
          <span
            className="text-primary cursor"
            onClick={() => fetchAddress(location, cacheKey)}
            style={{ cursor: "pointer" }}
          >
            {isLoading ? trans("Loading...") : trans("Show Address")}
          </span>
        )}
        {location.mapUrl ? (
          <a href={location.mapUrl} target="_blank" rel="noreferrer">
            {trans("View on Map")}
          </a>
        ) : null}
      </div>
    );
  };

  const columns = useMemo(
    () => [
    {
      title: trans("Code"),
      dataIndex: "employeeId",
      width: 150,
      fixed: !isRTL ? "left" : undefined,
      render: (name) => (
        <span className={"table-element-name ps-0"}>{name}</span>
      ),
    },
    {
      title: trans("Name"),
      dataIndex: "technicianName",
      width: 150,
      sorter: true,
      fixed: !isRTL ? "left" : undefined,
    },
    {
      title: trans("Job Title"),
      dataIndex: "jobTitle",
      width: 150,
      sorter: true,
    },
    {
      title: trans("Date"),
      dataIndex: "date",
      width: 150,
      fixed: !isRTL ? "left" : undefined,
    },
    // {
    //   title: trans("department"),
    //   dataIndex: "department",
    //   width: 150,
    //   sorter: true,
    // },
    {
      title: trans("Supervisor"),
      dataIndex: "supervisor",
      width: 150,
    },
    {
      title: trans("Assigned Shift"),
      dataIndex: "assignedShift",
      width: 250,
      render: (_, record) => record?.assignedShift || "0",
    },
    {
      title: trans("Actual Shift"),
      dataIndex: "shiftStartTime",
      width: 250,
      render: (_, record) => {
        const start = formatShiftClockTime(record?.shiftStartTime);
        const end = formatShiftClockTime(record?.shiftEndTime);
        if (start === EMPTY_VALUE && end === EMPTY_VALUE) return EMPTY_VALUE;
        return `${start} - ${end}`;
      },
    },
    {
      title: trans("Start Shift Location"),
      dataIndex: "startShiftLocation",
      width: 240,
      render: (location, record) => renderLocation(location, record, "start"),
    },
    {
      title: trans("End Shift Location"),
      dataIndex: "endShiftLocation",
      width: 240,
      render: (location, record) => renderLocation(location, record, "end"),
    },
    {
      title: trans("Start Shift Photo"),
      dataIndex: "startShiftPhoto",
      width: 140,
      render: (photo) =>
        photo?.url ? (
          <Image
            width={48}
            height={48}
            src={photo.url}
            alt={photo.name || trans("Start Shift Photo")}
            style={{ objectFit: "cover", borderRadius: 4 }}
            preview={{ mask: trans("View") }}
          />
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: trans("Early Leave Reason"),
      dataIndex: "earlyLeaveReason",
      width: 200,
      render: (reason) =>
        reason ? (
          <Typography.Text ellipsis={{ tooltip: reason }} style={{ maxWidth: 180 }}>
            {reason}
          </Typography.Text>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: trans("Work Hours"),
      dataIndex: "workMinutes",
      width: 150,
      render: (_, record) => record?.workHours || "0",
    },
    {
      title: trans("Early Leave"),
      dataIndex: "earlyLeaveMinutes",
      width: 150,
      render: (_, record) => {
        const limit = getLateLimitInMinutes(
          filters.lateThreshold,
          filters.lateUnit
        );
        const totalLate = record?.earlyLeaveMinutes || 0;
        const formatted = record?.earlyLeaveHours || "0";
        const isExceeded = limit !== null && totalLate > limit;

        return <span>{formatted}</span>;
      },
    },
    {
      title: trans("Late Hours"),
      dataIndex: "lateMinutes",
      width: 150,
      render: (_, record) => {
        const limit = getLateLimitInMinutes(
          filters.lateThreshold,
          filters.lateUnit
        );
        const totalLate = record?.lateMinutes || 0;
        const formatted = record?.lateHours || "0";
        const isExceeded = totalLate > limit;
        return (
          <span style={{ color: isExceeded ? "red" : "green" }}>
            {formatted}
          </span>
        );
      },
    },
    {
      title: trans("Overtime"),
      dataIndex: "overtimeMinutes",
      width: 150,
      render: (_, record) => (
        <Tag color={record?.overtimeMinutes >= 1 ? "green" : "default"}>
          {record?.overtimeHours || "0"}
        </Tag>
      ),
    },
  ],
    [addresses, filters.lateThreshold, filters.lateUnit, loadingAddress],
  );

  const visibleColumns = useReportTableColumns(columns);

  useEffect(() => {
    if (data) {
      setStartDateE(startDate);
      setEndDateE(endDate);
    }
  }, [data, startDate, endDate, selectedids]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      SetSortFilter({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      SetSortFilter({
        by: "",
        order: "",
      });
    }
  };

  const reportData = data?.technicians ? data : data?.data;
  const rows = (reportData?.technicians || []).map((row, index) => ({
    ...row,
    id: row.id ?? `${row.technicianId}-${row.date}-${index}`,
  }));

  return (
    <div className="bg-white-rounded-top mt-3 table-parent">
      <AntdTable
        columns={visibleColumns}
        dataSource={rows}
        loading={isLoading}
        pageSize={limit}
        totalItems={reportData?.totalResults || 0}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setCurrentPage(1);
        }}
        onTableChange={handleTableChange}
        scrollY={350}
      />
    </div>
  );
};

export default DailyAttendanceReportTable;
